import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { AgencyDashboardService } from '@/services/agencyDashboard.service';
import {
    AdvisorDetailResult,
    AgencyDashboardPeriod,
} from '@/integrations/supabase/rpc-types';

interface UseAdvisorDetailParams {
    /** `null` = pannello chiuso: nessuna chiamata parte. */
    advisorId: string | null;
    period: AgencyDashboardPeriod;
    periodOnly: boolean;
}

interface UseAdvisorDetailResult {
    data: AdvisorDetailResult | null;
    loading: boolean;
    error: string | null;
    /** La scheda non e' accessibile, o il consulente non esiste piu'. */
    notFound: boolean;
    refresh: () => Promise<void>;
}

/**
 * Scheda del singolo consulente, caricata all'apertura del pannello.
 *
 * Gemello di `useClientDetail`, e per lo stesso motivo non riusa la riga della tabella:
 * quella e' filtrata dal periodo, la scheda mostra di default lo storico completo.
 */
export function useAdvisorDetail({
    advisorId,
    period,
    periodOnly,
}: UseAdvisorDetailParams): UseAdvisorDetailResult {
    const [data, setData] = useState<AdvisorDetailResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notFound, setNotFound] = useState(false);
    const { t } = useI18n();

    const fetchData = useCallback(async () => {
        if (!advisorId) {
            setData(null);
            setNotFound(false);
            return;
        }

        setLoading(true);
        setError(null);
        setNotFound(false);

        try {
            const result = await AgencyDashboardService.getAdvisorDetail(
                advisorId,
                period,
                periodOnly
            );
            setData(result);
            setNotFound(result === null);
        } catch {
            const msg = String(t('errors.loadFailed'));
            setError(msg);
            toast({
                title: String(t('common.error')),
                description: msg,
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [advisorId, period, periodOnly]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return { data, loading, error, notFound, refresh: fetchData };
}

export default useAdvisorDetail;
